import { Mic, Square } from 'lucide-react';
import { type PointerEvent, useRef } from 'react';
import { cn } from '../../../lib/cn';
import { Spinner } from '../../../ui/Spinner';
import { type DictationState, formatElapsed } from '../dictation';

/**
 * Przycisk mikrofonu (Fala 10) przy polu wiadomości czatu Copilota: kliknij, żeby zacząć i skończyć,
 * albo przytrzymaj i mów (puszczenie kończy). Klawiatura: Enter / spacja = klik. Stan dla czytników ekranu przez
 * `aria-pressed` i nazwę („Mów” / „Zakończ dyktowanie”); komunikat na żywo pokazuje `DictationStatusLine`.
 * Okrągły przycisk 36 px obok pola wiadomości (wariant „pełna szerokość” zniknął z dawnym panelem głosowym, 10.10.2026).
 */

const HOLD_MS = 450;

export function MicButton({
  dictation: d,
  base = '',
  disabled = false,
  className,
  onStart,
}: {
  dictation: DictationState;
  /** tekst, do którego dopisujemy (zawartość pola przy starcie) */
  base?: string;
  disabled?: boolean;
  className?: string;
  /** przed startem (np. przestań podążać za ręczną edycją) */
  onStart?: () => void;
}) {
  const pressRef = useRef<{ at: number; started: boolean } | null>(null);
  const skipClickRef = useRef(false);
  const busy = d.status === 'transcribing';
  const live = d.status === 'listening' || d.status === 'recording';

  const begin = () => {
    onStart?.();
    d.start(base);
  };

  const toggle = () => {
    if (busy || disabled) return;
    if (live) d.stop();
    else begin();
  };

  // przytrzymaj, by mówić: start przy wciśnięciu, puszczenie po > 450 ms kończy; krótki klik = przełącznik
  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 || busy || disabled || live || !d.supported) return;
    pressRef.current = { at: Date.now(), started: true };
    begin();
  };
  const onPointerUp = () => {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press?.started) return;
    skipClickRef.current = true;
    if (Date.now() - press.at > HOLD_MS) d.stop();
  };
  const onClick = () => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    toggle();
  };

  const label = busy ? 'Przepisuję nagranie…' : live ? 'Zakończ dyktowanie' : 'Mów (kliknij albo przytrzymaj)';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={live}
      disabled={disabled || busy || !d.supported}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={() => {
        if (pressRef.current) onPointerUp();
      }}
      onClick={onClick}
      className={cn(
        'relative inline-flex size-9 shrink-0 touch-none items-center justify-center rounded-full transition-colors select-none',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:pointer-events-none disabled:opacity-50',
        live
          ? 'bg-danger text-on-accent'
          : 'border border-line bg-canvas text-fg-secondary hover:bg-surface hover:text-fg',
        className,
      )}
    >
      {busy ? (
        <Spinner size={16} />
      ) : live ? (
        <Square size={14} strokeWidth={2} aria-hidden fill="currentColor" />
      ) : (
        <Mic size={17} strokeWidth={1.75} aria-hidden />
      )}
      {live && (
        <span
          aria-hidden
          className="absolute inset-0 animate-ping rounded-full bg-danger opacity-25 motion-reduce:hidden"
        />
      )}
    </button>
  );
}

/**
 * Stan dyktowania jednym zdaniem (aria-live): „Słucham… Nagranie nie jest zapisywane.”, „Nagrywam 0:12 z 2:00…”,
 * „Przepisuję nagranie…”, komunikat błędu albo informacja o silniku. Puste, gdy nic się nie dzieje.
 */
export function DictationStatusLine({ dictation: d, className }: { dictation: DictationState; className?: string }) {
  let text: string | null = null;
  let tone: 'muted' | 'danger' | 'warning' = 'muted';
  if (d.status === 'listening') text = 'Słucham… Nagranie nie jest zapisywane.';
  else if (d.status === 'recording')
    text = `Nagrywam ${formatElapsed(d.elapsed)} z ${formatElapsed(d.maxSeconds)}… Nagranie nie jest zapisywane.`;
  else if (d.status === 'transcribing') text = 'Przepisuję nagranie…';
  else if (d.error) {
    text = d.error;
    tone = d.status === 'denied' ? 'warning' : 'danger';
  }
  const notice = d.notice && d.status !== 'error' ? d.notice : null;
  if (!text && !notice) return <span role="status" className="sr-only" />;
  return (
    <p
      role="status"
      className={cn(
        'flex items-center gap-2 text-caption',
        tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-fg-muted',
        className,
      )}
    >
      {(d.status === 'listening' || d.status === 'recording') && (
        <span aria-hidden className="relative inline-flex size-2">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-danger opacity-60 motion-reduce:hidden" />
          <span className="relative inline-flex size-2 rounded-full bg-danger" />
        </span>
      )}
      <span>{[text, notice].filter(Boolean).join(' ')}</span>
    </p>
  );
}
